import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Play, Pause, SkipBack, Trash2 } from 'react-feather';
import {
  TIMELINE_CUT_THUMB_HEIGHT,
  TIMELINE_STRIP_PADDING,
  TIMELINE_CUT_BORDER,
  TIMELINE_CUT_GAP,
  timelineCutEffectiveSeconds,
  timelineCutThumbWidth,
  timelineCutCardWidth,
  timelineCutsTrackWidth,
  sumTimelineEffectiveSeconds,
  timelinePlayheadLeft,
  timelineTimeFromTrackLeft,
  timelineAudioLaneWidth,
} from '../utils/timelineStrip';
import './TimelineCutStrip.css';

const CARD_HEIGHT = TIMELINE_CUT_THUMB_HEIGHT + TIMELINE_CUT_BORDER * 2;
const STRIP_HEIGHT = CARD_HEIGHT + TIMELINE_STRIP_PADDING * 2;
const AUDIO_LANE_HEIGHT = 26;
const AUDIO_LANE_GAP = 4;
const WAVE_BAR_WIDTH = 2;
const WAVE_BAR_GAP = 2;
const LONG_PRESS_MS = 180;
const EDGE_ZONE = 44;
const EDGE_STEP = 7;
const FOLLOW_TWEEN_MS = 220;
const SEEK_THROTTLE_MS = 80;

function formatTransportTime(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function offsetForItem(index, positions, cardWidths) {
  const usable = positions.length === cardWidths.length;
  const slot = usable ? positions[index] : index;
  let x = 0;
  for (let i = 0; i < cardWidths.length; i++) {
    const s = usable ? positions[i] : i;
    if (s < slot) x += cardWidths[i] + TIMELINE_CUT_GAP;
  }
  return x;
}

function slotForCenterX(centerX, positions, cardWidths) {
  const n = cardWidths.length;
  if (positions.length !== n) return 0;
  const slotWidths = new Array(n);
  for (let i = 0; i < n; i++) slotWidths[positions[i]] = cardWidths[i];
  let acc = 0;
  for (let s = 0; s < n; s++) {
    const w = slotWidths[s] + TIMELINE_CUT_GAP;
    if (centerX < acc + w) return s;
    acc += w;
  }
  return n - 1;
}

function movePosition(positions, from, to) {
  const next = positions.slice();
  for (let i = 0; i < next.length; i++) {
    const p = next[i];
    if (p === from) next[i] = to;
    else if (from < to && p > from && p <= to) next[i] = p - 1;
    else if (to < from && p >= to && p < from) next[i] = p + 1;
  }
  return next;
}

const FakeWaveform = React.memo(function FakeWaveform({ width }) {
  const count = Math.max(1, Math.floor(width / (WAVE_BAR_WIDTH + WAVE_BAR_GAP)));
  const bars = [];
  for (let i = 0; i < count; i++) {
    const t = Math.abs(Math.sin(i * 0.83 + Math.sin(i * 0.31) * 2.7));
    const h = Math.round((0.22 + 0.68 * t) * (AUDIO_LANE_HEIGHT - 8));
    bars.push(
      <div
        key={i}
        className="timeline-cut-strip__wave-bar"
        style={{ width: WAVE_BAR_WIDTH, height: Math.max(2, h) }}
      />
    );
  }
  return <div className="timeline-cut-strip__waveform">{bars}</div>;
});

function TimelineCard({
  cut,
  index,
  cardWidths,
  trackStart,
  positions,
  draggingIndex,
  dragX,
  scrollRef,
  fastPacing,
  onRemoveCut,
  renderThumb,
  onStartDrag,
  onDragMove,
  onEndDrag,
  setPositions,
  setDragX,
  containerRef,
  contentWidth,
  viewportWidth,
  suppressTransition,
}) {
  const pressTimerRef = useRef(null);
  const dragStartXRef = useRef(0);
  const dragStartScrollRef = useRef(0);
  const dragStartClientXRef = useRef(0);

  const thumbWidth = timelineCutThumbWidth(cut, fastPacing);
  const effectiveSec = timelineCutEffectiveSeconds(cut, fastPacing);
  const cardWidth = cardWidths[index];
  const isDragging = draggingIndex === index;

  const clearPressTimer = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  useEffect(() => () => clearPressTimer(), []);

  const restX = trackStart + offsetForItem(index, positions, cardWidths);
  const translateX = isDragging ? dragX : restX;

  return (
    <div
      className={`timeline-cut-strip__card ${isDragging ? 'timeline-cut-strip__card--dragging' : 'timeline-cut-strip__card--resting'}${suppressTransition ? ' timeline-cut-strip__card--no-transition' : ''}`}
      style={{
        width: cardWidth,
        height: CARD_HEIGHT,
        transform: `translateX(${translateX}px) scale(${isDragging ? 1.05 : 1})`,
      }}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        clearPressTimer();
        // React nulls currentTarget after the handler returns — capture before setTimeout.
        const target = e.currentTarget;
        const pointerId = e.pointerId;
        const clientX = e.clientX;
        pressTimerRef.current = setTimeout(() => {
          pressTimerRef.current = null;
          const startX = trackStart + offsetForItem(index, positions, cardWidths);
          dragStartXRef.current = startX;
          dragStartScrollRef.current = scrollRef.current?.scrollLeft ?? 0;
          dragStartClientXRef.current = clientX;
          onStartDrag(index, startX);
          if (target?.isConnected) {
            try { target.setPointerCapture(pointerId); } catch (_) {}
          }
        }, LONG_PRESS_MS);
      }}
      onPointerMove={(e) => {
        if (!isDragging) return;
        const scrollEl = scrollRef.current;
        const curScroll = scrollEl?.scrollLeft ?? 0;
        const deltaX = e.clientX - dragStartClientXRef.current + (curScroll - dragStartScrollRef.current);
        const x = dragStartXRef.current + deltaX;

        setPositions((prev) => {
          const centerX = x - trackStart + cardWidths[index] / 2;
          const target = slotForCenterX(centerX, prev, cardWidths);
          const current = prev[index];
          if (target !== current) return movePosition(prev, current, target);
          return prev;
        });

        setDragX(x);
        onDragMove(e.clientX, scrollEl, contentWidth, viewportWidth);
      }}
      onPointerUp={(e) => {
        clearPressTimer();
        if (isDragging) {
          try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
          onEndDrag();
        }
      }}
      onPointerCancel={(e) => {
        clearPressTimer();
        if (isDragging) {
          try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
          onEndDrag();
        }
      }}
    >
      <div className="timeline-cut-strip__thumb" style={{ width: thumbWidth, height: TIMELINE_CUT_THUMB_HEIGHT }}>
        {renderThumb ? renderThumb(cut, { width: thumbWidth, height: TIMELINE_CUT_THUMB_HEIGHT }) : (
          <video src={cut.url} muted playsInline preload="metadata" />
        )}
        <div className="timeline-cut-strip__label-gradient">
          <span className="timeline-cut-strip__label-text">{effectiveSec}:00</span>
        </div>
      </div>
      <button
        type="button"
        className="timeline-cut-strip__remove-btn"
        onClick={(e) => {
          e.stopPropagation();
          onRemoveCut(index);
        }}
        title="Remove"
        aria-label="Remove cut"
      >
        <Trash2 size={11} />
      </button>
    </div>
  );
}

/**
 * NLE-style editable timeline (CapCut-like transport) for web.
 * Mirrors mobile TimelineCutStrip: fixed center playhead, scroll-to-scrub,
 * transport controls, audio lane, and long-press drag-reorder.
 *
 * Imperative API (ref):
 * - scrollPlayheadPastCutMid(nextCuts, cutIndex): scroll the strip so the
 *   fixed playhead sits just past mid of that cut (next add lands after it).
 */
const TimelineCutStrip = forwardRef(function TimelineCutStrip({
  cuts,
  onCutsReorder,
  fastPacing = false,
  playheadTime = 0,
  showPlayhead = false,
  isPlaying = false,
  hasAudio = false,
  audioDuration = 0,
  onPlayPause,
  onSkipToStart,
  onScrubStart,
  onSeek,
  onScrubEnd,
  onRemoveCut,
  renderThumb,
}, ref) {
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const [viewportW, setViewportW] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [positions, setPositions] = useState(() => cuts.map((_, i) => i));
  const [draggingIndex, setDraggingIndex] = useState(-1);
  const [dragX, setDragX] = useState(0);
  const [suppressTransition, setSuppressTransition] = useState(false);

  const scrubActiveRef = useRef(false);
  const scrubEndTimerRef = useRef(null);
  const lastSeekAtRef = useRef(0);
  const isTouchingRef = useRef(false);
  const followRafRef = useRef(null);
  const followFromRef = useRef(0);
  const followToRef = useRef(0);
  const followStartRef = useRef(0);
  const edgeScrollRafRef = useRef(null);
  const dragClientXRef = useRef(0);
  const positionsRef = useRef(positions);
  const programmaticScrollRef = useRef(false);
  const suppressTransitionTimerRef = useRef(null);

  positionsRef.current = positions;

  const snapPositionsWithoutTransition = useCallback((nextPositions) => {
    setSuppressTransition(true);
    setPositions((prev) => {
      if (
        prev.length === nextPositions.length
        && prev.every((p, i) => p === nextPositions[i])
      ) {
        return prev;
      }
      return nextPositions;
    });
    if (suppressTransitionTimerRef.current) clearTimeout(suppressTransitionTimerRef.current);
    // Two frames so the identity layout paints before transitions re-enable.
    suppressTransitionTimerRef.current = setTimeout(() => {
      suppressTransitionTimerRef.current = null;
      setSuppressTransition(false);
    }, 50);
  }, []);

  useEffect(() => {
    // Cuts changed (reorder / add / remove): snap slots to identity without animating
    // from the previous drag mapping (that caused the "slides in from the right" glitch).
    snapPositionsWithoutTransition(cuts.map((_, i) => i));
  }, [cuts, fastPacing, snapPositionsWithoutTransition]);

  const cardWidths = useMemo(() => cuts.map((c) => timelineCutCardWidth(c, fastPacing)), [cuts, fastPacing]);
  const trackWidth = timelineCutsTrackWidth(cuts, fastPacing);
  const trackStart = viewportW / 2;
  const totalSeconds = sumTimelineEffectiveSeconds(cuts, fastPacing);
  const secCount = Math.max(1, Math.ceil(totalSeconds));
  const secWidth = trackWidth > 0 ? trackWidth / secCount : 72;
  const showAudioLane = hasAudio && cuts.length > 0;
  const scrollContentHeight = STRIP_HEIGHT + (showAudioLane ? AUDIO_LANE_HEIGHT + AUDIO_LANE_GAP : 0);
  const transportDuration = audioDuration > 0 ? audioDuration : totalSeconds;
  const audioLaneWidth = timelineAudioLaneWidth(cuts, audioDuration, fastPacing);
  const contentWidth = Math.max(trackWidth, audioLaneWidth) + viewportW;

  const keyCounts = {};
  const cardKeys = cuts.map((c) => {
    const id = c._id || c.id || 'cut';
    const n = keyCounts[id] || 0;
    keyCounts[id] = n + 1;
    return n === 0 ? `${id}` : `${id}#${n}`;
  });

  const pxToTime = useCallback(
    (px) => timelineTimeFromTrackLeft(cuts, px, fastPacing),
    [cuts, fastPacing]
  );

  const scrollToTrackPx = useCallback((px, { cutsForTime = null, pacing = fastPacing } = {}) => {
    const el = scrollRef.current;
    if (!el) return 0;
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    const nextPx = Math.max(0, Math.min(maxScroll, Number(px) || 0));
    const list = cutsForTime || cuts;
    const t = timelineTimeFromTrackLeft(list, nextPx, pacing);
    // Avoid handleScroll → onSeek re-entrancy while we set scrollLeft.
    programmaticScrollRef.current = true;
    el.scrollLeft = nextPx;
    setScrollLeft(nextPx);
    // Release on next frame after the scroll event has fired.
    requestAnimationFrame(() => {
      programmaticScrollRef.current = false;
    });
    onSeek?.(t);
    onScrubEnd?.(t);
    return t;
  }, [cuts, fastPacing, onSeek, onScrubEnd]);

  useImperativeHandle(ref, () => ({
    scrollToTrackPx,
    scrollPlayheadPastCutMid(nextCuts, cutIndex, pacing = fastPacing) {
      if (!nextCuts?.length || cutIndex < 0 || cutIndex >= nextCuts.length) return 0;
      let acc = 0;
      for (let i = 0; i < cutIndex; i++) {
        acc += timelineCutEffectiveSeconds(nextCuts[i], pacing);
      }
      const cutSec = timelineCutEffectiveSeconds(nextCuts[cutIndex], pacing);
      const targetTime = acc + cutSec * 0.55;
      const px = timelinePlayheadLeft(nextCuts, targetTime, pacing);
      return scrollToTrackPx(px, { cutsForTime: nextCuts, pacing });
    },
  }), [scrollToTrackPx, fastPacing]);

  const beginScrub = useCallback(() => {
    if (scrubEndTimerRef.current) {
      clearTimeout(scrubEndTimerRef.current);
      scrubEndTimerRef.current = null;
    }
    if (!scrubActiveRef.current) {
      scrubActiveRef.current = true;
      onScrubStart?.();
    }
  }, [onScrubStart]);

  const scrubUpdate = useCallback(
    (px) => {
      if (!scrubActiveRef.current) return;
      const now = Date.now();
      if (now - lastSeekAtRef.current < SEEK_THROTTLE_MS) return;
      lastSeekAtRef.current = now;
      onSeek?.(pxToTime(px));
    },
    [onSeek, pxToTime]
  );

  const finishScrub = useCallback(
    (px) => {
      if (scrubEndTimerRef.current) {
        clearTimeout(scrubEndTimerRef.current);
        scrubEndTimerRef.current = null;
      }
      if (!scrubActiveRef.current) return;
      scrubActiveRef.current = false;
      isTouchingRef.current = false;
      onScrubEnd?.(pxToTime(px));
    },
    [onScrubEnd, pxToTime]
  );

  const scheduleFinishScrub = useCallback(
    (px) => {
      if (scrubEndTimerRef.current) clearTimeout(scrubEndTimerRef.current);
      scrubEndTimerRef.current = setTimeout(() => finishScrub(px), 80);
    },
    [finishScrub]
  );

  const handleScroll = useCallback((e) => {
    const px = e.currentTarget.scrollLeft;
    setScrollLeft(px);
    if (programmaticScrollRef.current) return;
    if (draggingIndex !== -1) return;
    // During playback follow-scroll, time comes from audio — don't read scroll back.
    const isFollowScroll = isPlaying && !isTouchingRef.current && !scrubActiveRef.current;
    if (isFollowScroll) return;
    const now = Date.now();
    if (now - lastSeekAtRef.current < SEEK_THROTTLE_MS) return;
    lastSeekAtRef.current = now;
    onSeek?.(pxToTime(px));
  }, [onSeek, pxToTime, draggingIndex, isPlaying]);

  const handleScrollStart = useCallback(() => {
    if (draggingIndex !== -1) return;
    isTouchingRef.current = true;
    beginScrub();
  }, [beginScrub, draggingIndex]);

  const handleScrollEnd = useCallback(() => {
    if (draggingIndex !== -1) return;
    const el = scrollRef.current;
    if (el) scheduleFinishScrub(el.scrollLeft);
  }, [scheduleFinishScrub, draggingIndex]);

  useEffect(() => {
    if (!isPlaying || scrubActiveRef.current || draggingIndex !== -1 || !scrollRef.current) return;
    const targetPx = timelinePlayheadLeft(cuts, playheadTime, fastPacing);
    const el = scrollRef.current;
    followFromRef.current = el.scrollLeft;
    followToRef.current = targetPx;
    followStartRef.current = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - followStartRef.current) / FOLLOW_TWEEN_MS);
      const eased = followFromRef.current + (followToRef.current - followFromRef.current) * t;
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = eased;
        setScrollLeft(eased);
      }
      if (t < 1 && isPlaying && !scrubActiveRef.current && draggingIndex === -1) {
        followRafRef.current = requestAnimationFrame(tick);
      }
    };
    if (followRafRef.current) cancelAnimationFrame(followRafRef.current);
    followRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (followRafRef.current) cancelAnimationFrame(followRafRef.current);
    };
  }, [playheadTime, isPlaying, cuts, fastPacing, draggingIndex]);

  useEffect(() => () => {
    if (scrubEndTimerRef.current) clearTimeout(scrubEndTimerRef.current);
    if (followRafRef.current) cancelAnimationFrame(followRafRef.current);
    if (edgeScrollRafRef.current) cancelAnimationFrame(edgeScrollRafRef.current);
    if (suppressTransitionTimerRef.current) clearTimeout(suppressTransitionTimerRef.current);
  }, []);

  const commitOrder = useCallback(
    (finalPositions) => {
      const n = finalPositions.length;
      if (n !== cuts.length) return;
      const next = new Array(n);
      for (let i = 0; i < n; i++) next[finalPositions[i]] = cuts[i];
      if (next.some((c, s) => c !== cuts[s])) onCutsReorder(next);
    },
    [cuts, onCutsReorder]
  );

  const handleSkipToStart = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
      setScrollLeft(0);
    }
    onSkipToStart?.();
  }, [onSkipToStart]);

  const startDrag = useCallback((index, startX) => {
    setDraggingIndex(index);
    setDragX(startX);
  }, []);

  const handleDragMove = useCallback((clientX, scrollEl, cWidth, vpW) => {
    dragClientXRef.current = clientX;
    if (!scrollEl) return;
    const containerLeft = containerRef.current?.getBoundingClientRect().left || 0;
    const relX = clientX - containerLeft;
    const maxScroll = Math.max(0, cWidth - vpW);

    const step = () => {
      if (draggingIndex === -1) {
        edgeScrollRafRef.current = null;
        return;
      }
      const cur = scrollEl.scrollLeft;
      if (relX < EDGE_ZONE && cur > 0) {
        scrollEl.scrollLeft = Math.max(0, cur - EDGE_STEP);
        setScrollLeft(scrollEl.scrollLeft);
      } else if (relX > vpW - EDGE_ZONE && cur < maxScroll) {
        scrollEl.scrollLeft = Math.min(maxScroll, cur + EDGE_STEP);
        setScrollLeft(scrollEl.scrollLeft);
      }
      edgeScrollRafRef.current = requestAnimationFrame(step);
    };
    if (!edgeScrollRafRef.current) edgeScrollRafRef.current = requestAnimationFrame(step);
  }, [draggingIndex]);

  const endDrag = useCallback(() => {
    if (edgeScrollRafRef.current) {
      cancelAnimationFrame(edgeScrollRafRef.current);
      edgeScrollRafRef.current = null;
    }
    // Commit outside of setState updaters — calling parent setState inside a
    // positions updater caused "Maximum update depth exceeded".
    const pos = positionsRef.current;
    commitOrder(pos);
    // Snap to identity immediately (cuts will match after parent reorders).
    // Suppress CSS transition so neighbors don't animate from the stale mapping.
    snapPositionsWithoutTransition(pos.map((_, i) => i));
    setDraggingIndex(-1);
  }, [commitOrder, snapPositionsWithoutTransition]);

  const onContainerLayout = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setViewportW(el.clientWidth);
  }, []);

  useEffect(() => {
    onContainerLayout();
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(onContainerLayout)
      : null;
    if (ro && containerRef.current) ro.observe(containerRef.current);
    return () => ro?.disconnect();
  }, [onContainerLayout]);

  return (
    <div className="timeline-cut-strip">
      <div ref={containerRef} className="timeline-cut-strip__container">
        {hasAudio && (
          <div className="timeline-cut-strip__transport">
            <span className="timeline-cut-strip__transport-time">
              {formatTransportTime(playheadTime)} / {formatTransportTime(transportDuration)}
            </span>
            <button type="button" className="timeline-cut-strip__transport-btn timeline-cut-strip__transport-skip" onClick={handleSkipToStart} aria-label="Skip to start">
              <SkipBack size={14} />
            </button>
            <button type="button" className="timeline-cut-strip__transport-btn timeline-cut-strip__transport-play" onClick={onPlayPause} aria-label={isPlaying ? 'Pause' : 'Play'}>
              {isPlaying ? <Pause size={17} /> : <Play size={17} style={{ marginLeft: 2 }} />}
            </button>
          </div>
        )}

        <div
          ref={scrollRef}
          className={`timeline-cut-strip__scroll${draggingIndex !== -1 ? ' timeline-cut-strip__scroll--dragging' : ''}`}
          onScroll={handleScroll}
          onMouseDown={handleScrollStart}
          onMouseUp={handleScrollEnd}
          onMouseLeave={handleScrollEnd}
          onTouchStart={handleScrollStart}
          onTouchEnd={handleScrollEnd}
          onWheel={handleScrollStart}
        >
          <div className="timeline-cut-strip__content" style={{ width: contentWidth, height: scrollContentHeight }}>
            {cuts.map((cut, index) => (
              <TimelineCard
                key={cardKeys[index]}
                cut={cut}
                index={index}
                cardWidths={cardWidths}
                trackStart={trackStart}
                positions={positions}
                draggingIndex={draggingIndex}
                dragX={dragX}
                scrollRef={scrollRef}
                fastPacing={fastPacing}
                onRemoveCut={onRemoveCut}
                renderThumb={renderThumb}
                onStartDrag={startDrag}
                onDragMove={handleDragMove}
                onEndDrag={endDrag}
                setPositions={setPositions}
                setDragX={setDragX}
                containerRef={containerRef}
                contentWidth={contentWidth}
                viewportWidth={viewportW}
                suppressTransition={suppressTransition}
              />
            ))}
            {showAudioLane && (
              <div
                className="timeline-cut-strip__audio-lane"
                style={{ top: STRIP_HEIGHT, left: trackStart, width: audioLaneWidth }}
              >
                <FakeWaveform width={audioLaneWidth} />
              </div>
            )}
          </div>
        </div>

        {cuts.length > 0 && (
          <div className="timeline-cut-strip__ruler-viewport">
            <div
              className="timeline-cut-strip__ruler-track"
              style={{
                width: trackWidth,
                marginLeft: trackStart,
                transform: `translateX(${-scrollLeft}px)`,
              }}
            >
              {Array.from({ length: secCount }, (_, sec) => (
                <div key={`ruler-sec-${sec}`} className="timeline-cut-strip__ruler-sec" style={{ width: secWidth }}>
                  <span className="timeline-cut-strip__ruler-label">{sec}s</span>
                  {[1, 2, 3, 4].map((tick) => (
                    <div
                      key={tick}
                      className="timeline-cut-strip__ruler-tick"
                      style={{ left: `${(tick / 5) * 100}%` }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {showPlayhead && cuts.length > 0 && (
          <div className={`timeline-cut-strip__playhead${hasAudio ? ' timeline-cut-strip__playhead--with-transport' : ''}`} />
        )}
      </div>
    </div>
  );
});

export default TimelineCutStrip;
